import type { ApiGet } from "./apiFetch.js";
import { WorkoutNote } from "./schemas/workoutNote.js";

/**
 * One session's saved timeline notes, oldest first (#24). Each row is
 * validated; a malformed one is left out, never shown. Throws when the read
 * fails, so a caller can tell "no notes" from "could not read them".
 */
export async function fetchSessionNotes(
  apiGet: ApiGet,
  sessionId: string,
): Promise<WorkoutNote[]> {
  const res = await apiGet<unknown>(
    `/api/me/workout-notes?sessionId=${encodeURIComponent(sessionId)}`,
  );
  const items =
    res && typeof res === "object" ? (res as { items?: unknown }).items : undefined;
  if (!Array.isArray(items)) throw new Error("notes_unreadable");
  const notes: WorkoutNote[] = [];
  for (const raw of items) {
    const parsed = WorkoutNote.safeParse(raw);
    if (parsed.success) notes.push(parsed.data);
  }
  return notes;
}

/**
 * The notes a session opens with in the logger (#24): read for a correction
 * only, since a repeat is a new session. A failed read is `undefined`, never
 * `[]`, so the save deletes nothing it could not see.
 */
export function notesForEditing(
  apiGet: ApiGet,
  sessionId: string,
  mode: "edit" | "repeat",
): Promise<WorkoutNote[] | undefined> {
  if (mode !== "edit") return Promise.resolve(undefined);
  return fetchSessionNotes(apiGet, sessionId).catch(() => undefined);
}
